import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, LogOut, RefreshCw } from 'lucide-react';
import '../styles/AdminPage.css';
import { AdminUsersPanel } from './admin/AdminUsersPanel';
import { WaitlistPanel } from './admin/WaitlistPanel';
import { TestEmailTool } from './admin/TestEmailTool';
import { PretranslateTool } from './admin/PretranslateTool';
import { MergeLanguagesTool } from './admin/MergeLanguagesTool';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'icraftz_admin_token';

const TABS = [
  { id: 'users', label: 'Users' },
  { id: 'waitlist', label: 'Waitlist' },
  { id: 'account', label: 'Account' },
];

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [authChecked, setAuthChecked] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');

  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Tab
  const [activeTab, setActiveTab] = useState('waitlist');

  // Waitlist
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [entriesError, setEntriesError] = useState('');

  // Change password
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const verifyToken = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/api/admin/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setAdminEmail(res.data.email || '');
      return true;
    } catch (err) {
      return false;
    }
  }, []);

  const fetchEntries = useCallback(async (t) => {
    setLoadingEntries(true);
    setEntriesError('');
    try {
      const res = await axios.get(`${API}/api/admin/waitlist?limit=1000`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEntries(res.data || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setEntriesError(typeof detail === 'string' ? detail : 'Failed to load waitlist');
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
    } finally {
      setLoadingEntries(false);
    }
  }, []);

  // On mount: verify existing token + load the waitlist
  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setAuthChecked(true);
        return;
      }
      const ok = await verifyToken(token);
      if (ok) {
        await fetchEntries(token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
      setAuthChecked(true);
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API}/api/admin/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      const newToken = res.data.token;
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setAdminEmail(res.data.email || '');
      setPassword('');
      await fetchEntries(newToken);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    if (token) axios.post(`${API}/api/admin/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setEntries([]);
    setAdminEmail('');
  };

  const handleRefresh = () => {
    if (token) fetchEntries(token);
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (err) {
      return iso;
    }
  };

  // ----- Render states -----
  if (!authChecked) {
    return (
      <div className="admin-page admin-loading">
        <Loader2 className="admin-spinner" size={32} />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="admin-page admin-login-page">
        <div className="admin-login-card">
          <h1 className="admin-login-title">iCraftz<sup>™</sup> Admin</h1>
          <p className="admin-login-subtitle">Sign in to view the waitlist.</p>
          <form onSubmit={handleLogin} className="admin-login-form">
            <label className="admin-field">
              <span className="admin-field-label">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="admin-input"
                data-testid="admin-login-email"
              />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="admin-input"
                data-testid="admin-login-password"
              />
            </label>
            {loginError && (
              <p className="admin-login-error" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              className="admin-login-btn"
              disabled={loggingIn}
              data-testid="admin-login-submit"
            >
              {loggingIn ? <Loader2 className="admin-spinner-inline" size={16} /> : 'Sign In'}
            </button>
          </form>
          <p className="admin-login-forgot">
            <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link">
              Forgot your password?
            </Link>
          </p>
          <p className="admin-login-footer">Admin-only area. Unauthorised access is prohibited.</p>
        </div>
      </div>
    );
  }

  const isWaitlist = activeTab === 'waitlist';
  const isAccount = activeTab === 'account';
  const isUsers = activeTab === 'users';

  const headerTitle = isUsers ? 'Registered Users' : isWaitlist ? 'Waitlist Dashboard' : 'Account';

  const submitChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwNew.length < 12) {
      setPwError('New password must be at least 12 characters.');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/change-password`,
        { current_password: pwCurrent, new_password: pwNew },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPwSuccess(res.data.message || 'Password updated.');
      setPwCurrent('');
      setPwNew('');
      setPwConfirm('');
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Could not update password.');
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="admin-page admin-dashboard" data-testid="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1 className="admin-title">{headerTitle}</h1>
          <p className="admin-subtitle">
            Signed in as <strong>{adminEmail}</strong>
          </p>
        </div>
        <div className="admin-header-actions">
          {isWaitlist && (
          <button
            className="admin-action-btn admin-refresh-btn"
            onClick={handleRefresh}
            disabled={loadingEntries}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          )}
          <button
            className="admin-action-btn admin-logout-btn"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <nav className="admin-tabs" data-testid="admin-tabs">
        {TABS.map((tab) => {
          const count = tab.id === 'waitlist' ? entries.length : null;
          return (
            <button
              key={tab.id}
              type="button"
              className={`admin-tab${activeTab === tab.id ? ' admin-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.label}
              {count !== null && <span className="admin-tab-count">{count}</span>}
            </button>
          );
        })}
      </nav>

      {isUsers ? (
        <AdminUsersPanel token={token} />
      ) : isWaitlist ? (
        <WaitlistPanel entries={entries} setEntries={setEntries} loading={loadingEntries} error={entriesError} token={token} formatDate={formatDate} />
      ) : isAccount ? (
        <section className="admin-table-section admin-diagnostic-section">
          <div className="admin-diagnostic-card" data-testid="admin-account-card">
            <h3 className="admin-diagnostic-title">Change password</h3>
            <p className="admin-diagnostic-help">
              Rotate your admin password. The new password takes effect immediately.
              Minimum 12 characters — use a passphrase or a password manager.
            </p>
            <form className="admin-password-form" onSubmit={submitChangePassword}>
              <label className="admin-password-label">
                Current password
                <input
                  type="password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="admin-search-input"
                  data-testid="admin-pw-current"
                />
              </label>
              <label className="admin-password-label">
                New password
                <input
                  type="password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  className="admin-search-input"
                  data-testid="admin-pw-new"
                />
              </label>
              <label className="admin-password-label">
                Confirm new password
                <input
                  type="password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  className="admin-search-input"
                  data-testid="admin-pw-confirm"
                />
              </label>
              {pwError && (
                <p className="admin-error" data-testid="admin-pw-error">{pwError}</p>
              )}
              {pwSuccess && (
                <p className="admin-diagnostic-success" data-testid="admin-pw-success">{pwSuccess}</p>
              )}
              <button
                type="submit"
                className="admin-action-btn admin-refresh-btn"
                disabled={pwBusy}
                data-testid="admin-pw-submit"
              >
                {pwBusy ? <Loader2 className="admin-spinner" size={14} /> : 'Update password'}
              </button>
            </form>
          </div>
          <TestEmailTool token={token} />
          <PretranslateTool token={token} />
          <MergeLanguagesTool token={token} />
        </section>
      ) : null}
    </div>
  );
};

export default AdminPage;
